import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, TextField,
  IconButton, Tooltip, LinearProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Button,
} from '@mui/material';
import {
  LocalShipping as ReceivingIcon, Visibility, CheckCircle, Schedule, Search,
} from '@mui/icons-material';

const API = '/api/wmc';

const statusColors: Record<string, string> = {
  pending: '#F59E0B',
  inspecting: '#3B82F6',
  completed: '#10B981',
};

interface ReceivingRecord {
  id: string;
  receiving_number: string;
  supplier_name: string;
  po_number: string | null;
  status: string;
  received_date: string;
  received_by: string;
  dock_location: string | null;
  carrier: string | null;
  tracking_number: string | null;
  packing_slip_number: string | null;
  line_count: number;
  notes: string | null;
}

interface ReceivingDetail extends ReceivingRecord {
  lines: Array<{
    id: string;
    material_name: string | null;
    material_code: string | null;
    quantity_received: string;
    unit: string;
    supplier_lot_number: string | null;
    internal_lot_number: string | null;
    has_coc: boolean;
    has_coa: boolean;
    has_mtr: boolean;
    expiry_date: string | null;
  }>;
}

const WmcReceiving: React.FC = () => {
  const [records, setRecords] = useState<ReceivingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState<ReceivingDetail | null>(null);

  useEffect(() => {
    loadRecords();
  }, []);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/receiving/`);
      const data = await res.json();
      setRecords(data.results || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const openDetail = async (id: string) => {
    try {
      const res = await fetch(`${API}/receiving/${id}/`);
      const data = await res.json();
      setDetail(data);
    } catch (e) { console.error(e); }
  };

  const filtered = records.filter(r =>
    !search ||
    r.receiving_number.toLowerCase().includes(search.toLowerCase()) ||
    r.supplier_name.toLowerCase().includes(search.toLowerCase()) ||
    (r.po_number || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReceivingIcon /> Receiving
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <Card sx={{ borderLeft: '4px solid #F59E0B' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Pending</Typography>
              <Typography variant="h4" color="#F59E0B">
                {records.filter(r => r.status === 'pending').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card sx={{ borderLeft: '4px solid #3B82F6' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Inspecting</Typography>
              <Typography variant="h4" color="#3B82F6">
                {records.filter(r => r.status === 'inspecting').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card sx={{ borderLeft: '4px solid #10B981' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Completed</Typography>
              <Typography variant="h4" color="#10B981">
                {records.filter(r => r.status === 'completed').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TextField
        size="small"
        placeholder="Search receiving records..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        sx={{ mb: 2, width: 300 }}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} sx={{ backgroundColor: '#1F2937' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 'bold', color: '#9CA3AF', borderBottom: '2px solid #374151' } }}>
              <TableCell>Receiving #</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>PO #</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Received</TableCell>
              <TableCell>By</TableCell>
              <TableCell>Dock</TableCell>
              <TableCell>Carrier</TableCell>
              <TableCell>Lines</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(rec => (
              <TableRow key={rec.id} hover>
                <TableCell><Typography variant="body2" fontWeight="bold">{rec.receiving_number}</Typography></TableCell>
                <TableCell>{rec.supplier_name}</TableCell>
                <TableCell>{rec.po_number || '—'}</TableCell>
                <TableCell>
                  <Chip
                    label={rec.status}
                    size="small"
                    sx={{ backgroundColor: `${statusColors[rec.status] || '#6B7280'}20`, color: statusColors[rec.status] || '#6B7280', fontWeight: 'bold', textTransform: 'capitalize' }}
                  />
                </TableCell>
                <TableCell>{new Date(rec.received_date).toLocaleDateString()}</TableCell>
                <TableCell>{rec.received_by}</TableCell>
                <TableCell>{rec.dock_location || '—'}</TableCell>
                <TableCell>{rec.carrier || '—'}</TableCell>
                <TableCell align="center">{rec.line_count}</TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => openDetail(rec.id)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1F2937' }}>
          Receiving Record — {detail?.receiving_number}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1F2937' }}>
          {detail && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Supplier</Typography>
                  <Typography>{detail.supplier_name}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">PO</Typography>
                  <Typography>{detail.po_number || '—'}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Carrier / Tracking</Typography>
                  <Typography>{detail.carrier || '—'} {detail.tracking_number ? `/ ${detail.tracking_number}` : ''}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Packing Slip</Typography>
                  <Typography>{detail.packing_slip_number || '—'}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Dock Location</Typography>
                  <Typography>{detail.dock_location || '—'}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Received By</Typography>
                  <Typography>{detail.received_by} — {new Date(detail.received_date).toLocaleString()}</Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mb: 1 }}>Line Items</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Material</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Supplier Lot</TableCell>
                    <TableCell>Internal Lot</TableCell>
                    <TableCell>Expiry</TableCell>
                    <TableCell>CoC</TableCell>
                    <TableCell>CoA</TableCell>
                    <TableCell>MTR</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.lines.map(line => (
                    <TableRow key={line.id}>
                      <TableCell>
                        <Typography variant="body2">{line.material_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{line.material_code}</Typography>
                      </TableCell>
                      <TableCell>{line.quantity_received} {line.unit}</TableCell>
                      <TableCell>{line.supplier_lot_number || '—'}</TableCell>
                      <TableCell>{line.internal_lot_number || '—'}</TableCell>
                      <TableCell>{line.expiry_date || '—'}</TableCell>
                      <TableCell>{line.has_coc ? <CheckCircle fontSize="small" sx={{ color: '#10B981' }} /> : '—'}</TableCell>
                      <TableCell>{line.has_coa ? <CheckCircle fontSize="small" sx={{ color: '#10B981' }} /> : '—'}</TableCell>
                      <TableCell>{line.has_mtr ? <CheckCircle fontSize="small" sx={{ color: '#10B981' }} /> : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1F2937' }}>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WmcReceiving;
